/**
 * BulkEditModal
 *
 * Apply the same field update to every layer currently visible in the
 * LayerAdminExplorer (the filter set = the selection per #569). Admins
 * pick a field, set a value, see the count of affected layers, then
 * confirm before the PATCH /api/layers/bulk fires.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faExclamationTriangle,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { LayerService } from '../../../../services/layerService';
import type { Layer, LayerFilters } from '../../../../types/layer';
import './BulkEditModal.scss';

interface BulkEditModalProps {
  onClose: (success: boolean) => void;
  getFilters: () => LayerFilters;
}

type BulkField =
  | 'enabled'
  | 'default_opacity'
  | 'refresh_interval_minutes'
  | 'refresh_time_range';
type Step = 'compose' | 'confirm';

const TIME_RANGE_RE = /^([01]?\d|2[0-3]):[0-5]\d-([01]?\d|2[0-3]):[0-5]\d$/;

export const BulkEditModal: React.FC<BulkEditModalProps> = ({ onClose, getFilters }) => {
  const layerService = useMemo(() => new LayerService(), []);

  const [layers, setLayers] = useState<Layer[]>([]);
  const [loading, setLoading] = useState(true);
  const [field, setField] = useState<BulkField>('enabled');
  const [boolValue, setBoolValue] = useState<'true' | 'false'>('false');
  const [opacityValue, setOpacityValue] = useState<number>(1);
  const [refreshMinutes, setRefreshMinutes] = useState<number>(5);
  const [refreshRange, setRefreshRange] = useState<string>('');
  const [step, setStep] = useState<Step>('compose');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    layerService
      .getAllLayers(getFilters())
      .then((rows) => {
        if (!cancelled) setLayers(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load layers');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layerService, getFilters]);

  const count = layers.length;
  const updates: {
    enabled?: boolean;
    default_opacity?: number;
    refresh_interval_minutes?: number;
    refresh_time_range?: string;
  } = (() => {
    switch (field) {
      case 'enabled':
        return { enabled: boolValue === 'true' };
      case 'default_opacity':
        return { default_opacity: opacityValue };
      case 'refresh_interval_minutes':
        return { refresh_interval_minutes: refreshMinutes };
      case 'refresh_time_range':
        return { refresh_time_range: refreshRange };
    }
  })();

  const valueLabel = (() => {
    switch (field) {
      case 'enabled':
        return boolValue === 'true' ? 'Enable' : 'Disable';
      case 'default_opacity':
        return `Opacity ${opacityValue}`;
      case 'refresh_interval_minutes':
        return `Every ${refreshMinutes} min`;
      case 'refresh_time_range':
        return `${refreshRange} NWT`;
    }
  })();

  // Compose-step validity for the Apply button. Mirrors the server-side
  // validation in src/backend/services/layerService.ts:bulkUpdate so the
  // user can't get to the confirm step with garbage.
  const composeValid = (() => {
    switch (field) {
      case 'refresh_interval_minutes':
        return (
          Number.isFinite(refreshMinutes) && refreshMinutes > 0 && refreshMinutes <= 1440
        );
      case 'refresh_time_range':
        return TIME_RANGE_RE.test(refreshRange);
      default:
        return true;
    }
  })();

  const handleApply = () => {
    setError(null);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setApplying(true);
    setError(null);
    try {
      const ids = layers.map((l) => l.id);
      await layerService.bulkUpdate(ids, updates);
      onClose(true);
    } catch (err: any) {
      setError(err.message || 'Bulk update failed');
      setStep('compose');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bulk-edit-modal-backdrop" role="dialog" aria-modal="true">
      <div className="bulk-edit-modal">
        <header className="bulk-edit-modal__header">
          <FontAwesomeIcon icon={faPenToSquare} />
          <h2>Bulk Edit Layers</h2>
        </header>

        <section className="bulk-edit-modal__body">
          {loading ? (
            <p>
              <FontAwesomeIcon icon={faRotate} spin /> Loading filtered layers…
            </p>
          ) : (
            <p className="bulk-edit-modal__count">
              <strong>{count}</strong> layer{count === 1 ? '' : 's'} match the current filter
              {count === 0 ? ' — nothing to update.' : '.'}
            </p>
          )}

          {step === 'compose' && (
            <>
              <label>
                Field
                <select
                  value={field}
                  onChange={(e) => setField(e.target.value as BulkField)}
                  aria-label="Field"
                >
                  <option value="enabled">Enabled</option>
                  <option value="default_opacity">Default opacity</option>
                  <option value="refresh_interval_minutes">Refresh interval (minutes)</option>
                  <option value="refresh_time_range">Refresh time range (NWT)</option>
                </select>
              </label>

              {field === 'enabled' && (
                <label>
                  Value
                  <select
                    value={boolValue}
                    onChange={(e) => setBoolValue(e.target.value as 'true' | 'false')}
                    aria-label="Value"
                  >
                    <option value="false">Disable (enabled = false)</option>
                    <option value="true">Enable (enabled = true)</option>
                  </select>
                </label>
              )}

              {field === 'default_opacity' && (
                <label>
                  Value
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={opacityValue}
                    onChange={(e) => setOpacityValue(Number(e.target.value))}
                    aria-label="Value"
                  />
                </label>
              )}

              {field === 'refresh_interval_minutes' && (
                <label>
                  Minutes
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    step={1}
                    value={refreshMinutes}
                    onChange={(e) => setRefreshMinutes(Number(e.target.value))}
                    aria-label="Refresh interval in minutes"
                  />
                  <small className="form-help">
                    Stored as <code>config.refreshInterval</code> in milliseconds. Range
                    1–1440 minutes (24h). The polling system added in v0.3.42 reads this
                    on every tick.
                  </small>
                </label>
              )}

              {field === 'refresh_time_range' && (
                <label>
                  NWT time range
                  <input
                    type="text"
                    placeholder="13:30-14:00"
                    value={refreshRange}
                    onChange={(e) => setRefreshRange(e.target.value)}
                    aria-label="Refresh time range"
                  />
                  <small className="form-help">
                    Format <code>HH:MM-HH:MM</code> in 24h America/Yellowknife time. Cross-midnight
                    ranges (e.g. <code>22:00-06:00</code>) are supported. Stored as
                    <code>config.refreshTimeRange</code>; the polling system only fires within
                    the window.
                  </small>
                </label>
              )}
            </>
          )}

          {step === 'confirm' && (
            <div className="bulk-edit-modal__confirm" role="alert">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <p>
                About to set <strong>{field}</strong> to <strong>{valueLabel}</strong> on{' '}
                <strong>{count}</strong> layer{count === 1 ? '' : 's'}. This cannot be undone in
                bulk — proceed?
              </p>
            </div>
          )}

          {error && (
            <p className="bulk-edit-modal__error" role="alert">
              {error}
            </p>
          )}
        </section>

        <footer className="bulk-edit-modal__footer">
          {step === 'compose' && (
            <>
              <button className="btn-secondary" onClick={() => onClose(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleApply}
                disabled={loading || count === 0 || !composeValid}
              >
                Apply
              </button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <button
                className="btn-secondary"
                onClick={() => setStep('compose')}
                disabled={applying}
              >
                Back
              </button>
              <button className="btn-primary" onClick={handleConfirm} disabled={applying}>
                {applying ? 'Applying…' : 'Confirm'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};
